import { ReactElement } from 'react'
// @ts-ignore
import currencyFormatter from 'currency-formatter'
import styled from 'styled-components'
import { AdvanceToUnlock, EventDebits } from '../../types/event-debits'

const toBRL = (value: number): string =>
  currencyFormatter.format(value, { code: 'BRL' })

const NoticeBox = styled.div<{ variant: 'warning' | 'success' }>`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 24px;
  border: 1px solid
    ${({ variant }) => (variant === 'warning' ? '#f0dcae' : '#cfe2d8')};
  background: ${({ variant }) =>
    variant === 'warning' ? '#fdf3dd' : '#e8f4ee'};
  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: ${({ variant }) => (variant === 'warning' ? '#8a5b00' : '#1e7a4f')};
    b {
      font-variant-numeric: tabular-nums;
    }
  }
`

const NoticeIcon = styled.span<{ variant: 'warning' | 'success' }>`
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 13px;
  color: #fff;
  background: ${({ variant }) =>
    variant === 'warning' ? '#8a5b00' : '#1e7a4f'};
`

interface AdvanceDebitsNoticeProps {
  debits: EventDebits | undefined
  advanceToUnlock: AdvanceToUnlock | undefined
}

export function AdvanceDebitsNotice({
  debits,
  advanceToUnlock,
}: AdvanceDebitsNoticeProps): ReactElement | null {
  if (debits === undefined || debits.remaining <= 0) {
    return null
  }

  if (advanceToUnlock?.coverable && advanceToUnlock.minGross !== null) {
    return (
      <NoticeBox variant="success">
        <NoticeIcon variant="success">✓</NoticeIcon>
        <p>
          Faltam <b>{toBRL(debits.remaining)}</b> para cobrir os débitos deste
          evento. Antecipando pelo menos <b>{toBRL(advanceToUnlock.minGross)}</b>{' '}
          os débitos ficam cobertos e todo valor antecipado acima disso vira
          saldo disponível para saque.
        </p>
      </NoticeBox>
    )
  }

  return (
    <NoticeBox variant="warning">
      <NoticeIcon variant="warning">!</NoticeIcon>
      <p>
        <b>Este evento tem {toBRL(debits.remaining)} em débitos a cobrir.</b>{' '}
        O valor antecipado será usado primeiro para abater esses débitos e não
        ficará disponível para saque enquanto eles não forem totalmente
        cobertos.
      </p>
    </NoticeBox>
  )
}

interface AdvanceSimulationOutcomeProps {
  debits: EventDebits | undefined
  netAdvanced: number
  visible: boolean
}

export function AdvanceSimulationOutcome({
  debits,
  netAdvanced,
  visible,
}: AdvanceSimulationOutcomeProps): ReactElement | null {
  if (!visible || debits === undefined || debits.remaining <= 0) {
    return null
  }

  if (netAdvanced < debits.remaining) {
    const remainingAfter = debits.remaining - netAdvanced
    return (
      <NoticeBox variant="warning">
        <NoticeIcon variant="warning">!</NoticeIcon>
        <p>
          <b>Esta antecipação não vai gerar saldo disponível.</b> O líquido de{' '}
          <b>{toBRL(netAdvanced)}</b> abaterá os débitos do evento: restarão{' '}
          <b>{toBRL(remainingAfter)}</b> a cobrir e o Saldo Disponível
          continuará <b>R$ 0,00</b>.
        </p>
      </NoticeBox>
    )
  }

  const availableAfter = netAdvanced - debits.remaining
  return (
    <NoticeBox variant="success">
      <NoticeIcon variant="success">✓</NoticeIcon>
      <p>
        Esta antecipação cobre os débitos do evento e libera{' '}
        <b>{toBRL(availableAfter)}</b> como saldo disponível para saque.
      </p>
    </NoticeBox>
  )
}
